import { createContext, useEffect, useState } from "react";
import { CheckCircle2, XCircle, Loader2, AlertTriangle } from "lucide-react";

type Status = "success" | "error" | "loading" | "confirm";

type State = {
  open: boolean;
  status: Status;
  message?: string;
  onConfirm?: () => void;
};

type Ctx = {
  showSuccess: (message?: string) => void;
  showError: (message?: string) => void;
  showLoading: (message?: any) => void;
  confirm: (message: string) => Promise<boolean>;
};

export const ActionModalContext = createContext<Ctx | null>(null);

export const ActionModalProvider = ({ children }: { children: any }) => {
  const [state, setState] = useState<State>({
    open: false,
    status: "loading",
  });

  useEffect(() => {
    if (!state.open) return;
    if (state.status === "loading" || state.status === "confirm") return;

    const t = setTimeout(() => setState((x) => ({ ...x, open: false })), 1800);

    return () => clearTimeout(t);
  }, [state]);

  const confirm = (message: string) =>
    new Promise<boolean>((resolve) => {
      setState({
        open: true,
        status: "confirm",
        message,
        onConfirm: () => resolve(true),
      });

      const cancel = () => resolve(false);

      (confirm as any)._cancel = cancel;
    });

  const value: Ctx = {
    showLoading: (message) =>
      setState({ open: true, status: "loading", message }),
    showSuccess: (message) =>
      setState({ open: true, status: "success", message }),
    showError: (message) => setState({ open: true, status: "error", message }),
    confirm,
  };

  const Icon =
    state.status === "success"
      ? CheckCircle2
      : state.status === "error"
      ? XCircle
      : state.status === "confirm"
      ? AlertTriangle
      : Loader2;

  const color =
    state.status === "success"
      ? "#10B981"
      : state.status === "error"
      ? "#EF4444"
      : state.status === "confirm"
      ? "#F59E0B"
      : "#3B82F6";

  return (
    <ActionModalContext.Provider value={value}>
      {children}

      {state.open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(2,6,23,.55)",
            backdropFilter: "blur(6px)",
            zIndex: 9999999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: 300,
              padding: "26px 22px",
              borderRadius: 18,
              background: "linear-gradient(180deg,#fff,#f8fafc)",
              boxShadow: "0 25px 60px rgba(0,0,0,.25)",
              textAlign: "center",
              animation: "scaleIn .25s ease",
            }}
          >
            <Icon
              size={54}
              color={color}
              style={{
                marginBottom: 12,
                animation:
                  state.status === "loading"
                    ? "spin 1s linear infinite"
                    : "pop .35s ease",
              }}
            />

            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: "#0f172a",
              }}
            >
              {state.status === "confirm"
                ? "Are you sure?"
                : state.status === "success"
                ? "Operation Successful"
                : state.status === "error"
                ? "Operation Failed"
                : "Processing"}
            </div>

            {state.message && (
              <div
                style={{
                  marginTop: 6,
                  fontSize: 13,
                  color: "#475569",
                }}
              >
                {state.message}
              </div>
            )}

            {state.status === "confirm" && (
              <div
                style={{
                  marginTop: 16,
                  display: "flex",
                  gap: 10,
                  justifyContent: "center",
                }}
              >
                <button
                  onClick={() => {
                    state.onConfirm?.();
                    setState((x) => ({ ...x, open: false }));
                  }}
                  style={{
                    padding: "8px 16px",
                    borderRadius: 8,
                    border: "none",
                    background: "#10B981",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Confirm
                </button>

                <button
                  onClick={() => {
                    (confirm as any)._cancel?.();
                    setState((x) => ({ ...x, open: false }));
                  }}
                  style={{
                    padding: "8px 16px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    background: "#fff",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          <style>
            {`
              @keyframes pop {
                0% { transform: scale(.6); opacity: 0 }
                100% { transform: scale(1); opacity: 1 }
              }
              @keyframes scaleIn {
                0% { transform: scale(.92); opacity: 0 }
                100% { transform: scale(1); opacity: 1 }
              }
              @keyframes spin {
                to { transform: rotate(360deg) }
              }
            `}
          </style>
        </div>
      )}
    </ActionModalContext.Provider>
  );
};
